import { Control, Controller, RegisterOptions, useForm } from "react-hook-form";
import InputField from "@/components/ui/input-field";
import { Button } from "@/components/ui/button";
import TextareaField from "@/components/ui/textarea-field";
import { useLocale, useTranslations } from "next-intl";
import { FeedbackResource, FeedbackResourceFormEnum } from "@/api/generated-api";
import { useMutation } from "@tanstack/react-query";
import { ApiContacts } from "@/api/contacts";
import { FC, useEffect } from "react";
import S from "./index.module.scss";
import { openErrorNotification, openSuccessFormNotification } from "@/utils/helpers";

const defaultFields = {
  full_name: "",
  email: "",
  phone: "",
  country: "",
  city: "",
  message: "",
  form: FeedbackResourceFormEnum.Contacts,
} satisfies FeedbackResource;

interface IInputFieldController<K extends keyof FeedbackResource> {
  label: string;
  required?: boolean;
  placeholder?: string;
  name: K;
  control: Control<FeedbackResource>;
  rules?: RegisterOptions<FeedbackResource, K>;
}

const InputFieldController = <K extends keyof FeedbackResource>({
  control,
  name,
  placeholder,
  rules,
  required = false,
  label,
}: IInputFieldController<K>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <InputField
          id={field.name}
          label={label}
          type="text"
          value={field.value}
          placeholder={placeholder}
          required={required}
          error={fieldState.error?.message || ""}
          onChange={field.onChange}
        />
      )}
    />
  );
};

interface ISupportForm {
  title?: string;
  form: FeedbackResourceFormEnum;
}

const SupportForm: FC<ISupportForm> = ({ title, form }) => {
  const t = useTranslations();
  const lang = useLocale();
  const mutation = useMutation({
    mutationFn: (contacts: FeedbackResource) => {
      return ApiContacts.postContactForm(contacts, lang);
    },
  });

  const { control, handleSubmit } = useForm<FeedbackResource>({ defaultValues: defaultFields });

  const onSubmit = (data: FeedbackResource) => {
    mutation.mutateAsync({ ...data, form });
  };

  useEffect(() => {
    if (mutation.isError) {
      openErrorNotification({ message: t("notification.error"), description: mutation.error.message });
    } else if (mutation.isSuccess) {
      openSuccessFormNotification({ message: t("notification.success") });
    }
  }, [mutation, t]);

  return (
    <form className={S.contentForm}>
      <h1 className={S.title}>{title}</h1>
      <div className={S.inputsGrid}>
        <InputFieldController
          control={control}
          name="full_name"
          required
          placeholder="Ex.: Alex Smith"
          rules={{
            required: "Name is required",
          }}
          label={t("form.firstNameSurname")}
        />

        <InputFieldController
          control={control}
          name="email"
          required
          placeholder="Ex.: you@gmail.com"
          rules={{
            required: "Email is required",
            pattern: {
              value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
              message: "Enter a valid email",
            },
          }}
          label={t("form.email")}
        />

        <InputFieldController
          control={control}
          name="phone"
          placeholder="Ex.: +49 00 00 00 00 00"
          label={t("form.phone")}
        />

        <InputFieldController control={control} name="country" placeholder="Ex.: Denmark" label={t("form.country")} />

        <InputFieldController control={control} name="city" label={t("form.city")} />
      </div>

      <Controller
        name="message"
        control={control}
        render={({ field, fieldState }) => (
          <TextareaField
            id={field.name}
            label={t("form.question")}
            value={field.value}
            error={fieldState.error?.message || ""}
            onChange={field.onChange}
          />
        )}
      />

      <Button onClick={handleSubmit(onSubmit)} type="submit">
        {t("form.send")}
      </Button>

      <span className={S.confirmText}>{t("form.confirm")}</span>
    </form>
  );
};

export default SupportForm;
